// File: src/components/fans/FanUnlockModal.js
import React from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Badge Tier Color Mapping Configuration
 */
const TIER_COLORS = {
  FAN: {
    text: "text-green-500",
    border: "border-green-500/40",
    glow: "shadow-[0_0_50px_rgba(34,197,94,0.3)]",
    badgeBg: "bg-green-500/20",
    badgeBorder: "border-green-500",
  },
  SUPAFAN: {
    text: "text-blue-500",
    border: "border-blue-500/40",
    glow: "shadow-[0_0_50px_rgba(59,130,246,0.3)]",
    badgeBg: "bg-blue-500/20",
    badgeBorder: "border-blue-500",
  },
  ULTRAFAN: {
    text: "text-[#ff00cc]",
    border: "border-[#ff00cc]/40",
    glow: "shadow-[0_0_50px_rgba(255,0,204,0.3)]",
    badgeBg: "bg-[#ff00cc]/20",
    badgeBorder: "border-[#ff00cc]",
  },
};

/**
 * FanUnlockModal Component
 * Displays a celebratory popup when a user unlocks a Fan status tier.
 */
const FanUnlockModal = ({ 
  isOpen, 
  onClose, 
  artistName = "this artist", 
  artistId, 
  artistPhotoURL,
  badgeTier = "FAN",
  onExploreExclusive 
}) => {
  if (!isOpen) return null;

  // Normalize badge tier string to match TIER_COLORS key
  const normalizedTier = (badgeTier || "FAN").toUpperCase();
  const activeColor = TIER_COLORS[normalizedTier] || TIER_COLORS.FAN;

  const handleExploreClick = () => {
    if (onExploreExclusive) {
      onExploreExclusive(artistId);
    } else {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 bg-black/80 z-[400] flex items-center justify-center p-4 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div 
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          className={`bg-zinc-900 border ${activeColor.border} text-white rounded-3xl p-6 md:p-8 w-full max-w-sm text-center relative ${activeColor.glow}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Animated Celebration Icon */}
          <motion.div 
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 3 }}
            className={`w-20 h-20 mx-auto rounded-full p-1 border-2 ${activeColor.badgeBorder} ${activeColor.badgeBg} mb-4 shadow-xl relative flex items-center justify-center overflow-hidden`}
          >
            {artistPhotoURL ? (
              <img 
                src={artistPhotoURL} 
                alt={artistName} 
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <div className="w-full h-full rounded-full bg-zinc-800 flex items-center justify-center font-black text-xl text-zinc-400 uppercase">
                {artistName.charAt(0)}
              </div>
            )}
          </motion.div>

          {/* Title Header */}
          <h2 className="text-2xl font-black italic tracking-tight text-white uppercase">
            <span className={activeColor.text}>{badgeTier}</span> Status Unlocked!
          </h2>

          <p className={`text-xs font-bold ${activeColor.text} uppercase tracking-widest mt-1`}>
            Official {badgeTier} of {artistName}
          </p>

          {/* Description Box */}
          <div className="my-5 p-4 bg-zinc-800/80 border border-zinc-700/80 rounded-2xl text-xs font-medium text-zinc-300 leading-relaxed space-y-2">
            <p>
              <strong className="text-white">Congratulations!</strong> You’ve officially earned <strong className={activeColor.text}>{badgeTier} Status</strong>.
            </p>
            <p className="text-zinc-400">
              You now have exclusive access to <strong className="text-white">{artistName}’s Fan-Only content</strong>!
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2 pt-1">
            <button
              onClick={handleExploreClick}
              className="w-full bg-[#ff00cc] hover:bg-[#e000b4] text-white py-3.5 px-4 rounded-xl font-black uppercase tracking-wider text-xs shadow-lg transition-all active:scale-95"
            >
              Explore Exclusive Content
            </button>

            <button
              onClick={onClose}
              className="w-full py-2.5 px-4 rounded-xl font-bold text-xs text-zinc-400 hover:text-white transition-colors"
            >
              Continue Browsing
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default FanUnlockModal;